'use client';

import React, { useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Users,
  Trophy,
  Target,
  Code,
  Laptop,
  Play,
  TrendingUp,
  MessageSquare,
  Clock,
  ChevronDown,
  Menu,
  X,
  Star
} from 'lucide-react';
import Link from 'next/link';
import { Card, Button, Badge } from '../components/ui';

// --- Data Constants ---
const features = [
  { icon: Code, title: 'Real-World Projects', description: 'Work on actual industry projects that make a difference and build your portfolio.' },
  { icon: Users, title: 'Expert Mentorship', description: 'Learn from experienced professionals who guide you throughout your journey.' },
  { icon: Trophy, title: 'Skill Development', description: 'Master in-demand technologies and frameworks used by top companies.' },
  { icon: Target, title: 'Career Growth', description: 'Build connections and gain experience that accelerates your career path.' }
];

const stats = [
  { number: '500+', label: 'Students Trained' },
  { number: '50+', label: 'Industry Projects' },
  { number: '95%', label: 'Placement Rate' },
  { number: '4.8/5', label: 'Student Rating' }
];

const testimonials = [
  { name: 'Sarah Johnson', role: 'Frontend Developer at Google', content: 'StartupWorld gave me the real-world experience I needed. The mentorship was exceptional!', rating: 5 },
  { name: 'Michael Chen', role: 'Full Stack Developer at Microsoft', content: 'The project-based learning approach helped me land my dream job. Highly recommended!', rating: 5 },
  { name: 'Emily Davis', role: 'UI/UX Designer at Apple', content: 'Amazing program with great mentors. I learned more here than in my entire college course.', rating: 5 }
];

const programs = [
  { title: 'Web Development', duration: '3 Months', level: 'Beginner to Advanced', technologies: ['React', 'Node.js', 'MongoDB', 'AWS'], color: 'from-blue-500 to-purple-600' },
  { title: 'Mobile Development', duration: '4 Months', level: 'Intermediate', technologies: ['React Native', 'Flutter', 'Firebase', 'APIs'], color: 'from-green-500 to-teal-600' },
  { title: 'Data Science', duration: '4 Months', level: 'Advanced', technologies: ['Python', 'TensorFlow', 'SQL', 'Tableau'], color: 'from-purple-500 to-pink-600' }
];

// --- Sub-Components ---

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                StartupWorld
              </h1>
            </div>
          </div>

          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Features</a>
              <a href="#programs" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Programs</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Reviews</a>

              <div className="relative">
                <button onClick={() => { setIsRegisterOpen(!isRegisterOpen); setIsLoginOpen(false); }} className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">
                  Register <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isRegisterOpen ? "rotate-180" : ""}`} />
                </button>
                {isRegisterOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-white shadow-lg border rounded-lg py-2 z-50">
                    <Link href="/student/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Student</Link>
                    <Link href="/college/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register College</Link>
                    <Link href="/mentor/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Mentor</Link>
                  </div>
                )}
              </div>

              <div className="relative">
                <button onClick={() => { setIsLoginOpen(!isLoginOpen); setIsRegisterOpen(false); }} className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">
                  Login <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isLoginOpen ? "rotate-180" : ""}`} />
                </button>
                {isLoginOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg border rounded-lg py-2 z-50">
                    <Link href="/student/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50">Student Login</Link>
                    <Link href="/staff/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Staff Login</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

const Hero = () => (
  <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div className="max-w-7xl mx-auto px-4 text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Badge className="mb-4 bg-blue-100 text-blue-800 px-4 py-2">🚀 India's Premier Internship Platform</Badge>
        <h1 className="text-4xl md:text-6xl font-bold mb-6">Transform Your <span className="text-blue-600">Career</span> with Experience</h1>
        <div className="flex justify-center gap-4 mt-8">
          <Button size="lg" className="bg-blue-600 text-white">Get Started</Button>
        </div>
      </motion.div>
    </div>
  </section>
);

// --- Main Home Component ---

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      {/* We wrap the entire home content in Suspense. 
          This prevents Next.js from failing during 'npm run build' 
          if any child component uses searchParams.
      */}
      <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading StartupWorld...</div>}>
        <Navbar />
        <Hero />
        
        <section id="features" className="py-20">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <Card key={i} className="p-6 text-center shadow-sm">
                <f.icon className="mx-auto mb-4 text-blue-600" />
                <h3 className="font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600">{f.description}</p>
              </Card>
            ))}
          </div>
        </section>

        <section id="programs" className="py-20 bg-gray-50">
           <div className="max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-3xl font-bold mb-12">Our Programs</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                {programs.map((p, i) => (
                  <Card key={i} className="p-6">
                    <div className={`h-2 w-full mb-4 bg-gradient-to-r ${p.color} rounded`} />
                    <h3 className="text-xl font-bold mb-2">{p.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">{p.duration} • {p.level}</p>
                    <Button variant="outline" className="w-full">View Details</Button>
                  </Card>
                ))}
              </div>
           </div>
        </section>

        <footer className="bg-gray-900 text-white py-12 text-center">
          <p>© 2026 StartupWorld. All rights reserved.</p>
        </footer>
      </Suspense>
    </div>
  );
}